import { useEffect, useRef, useState } from 'react';
import { useCurrentUserId } from '@framework/auth';
import type { WorkspaceEntityRepository, Workspace } from '@workspace/domain/workspace';
import { createWorkspaceEntityRepository } from '@workspace/domain/workspace';
import type { FolderId, ViewModelId, WorkspaceId, ViewModelJSON, FolderJSON } from 'balus-common';
import { ObjectRepository } from '@framework/repository';
import { RTDBPath } from 'balus-common';
import { RootFolderTreeRepository } from '@workspace/view-model-folder/infrastructure/FolderTree';
import { ViewModelEntity } from '@view-model/domain/view-model';
import { Folder } from '@workspace/view-model-folder/domain/Folder';
import type { RootFolderTree } from '@workspace/view-model-folder/domain/FolderTree';
import { KeysRepository } from '@framework/repository/Collection';
import { usePrevious } from '@view-model/models/common/hooks/usePrevious';
import { GroupIpRestrictionCheckRequest } from '@group/AccessControl';
import { useCurrentSessionId } from '@framework/auth/AuthContext';
import { useSetAtom, useAtomValue } from 'jotai';
import {
  setInitialAllSearchRecordsAtom,
  addWorkspaceInAllSearchAtom,
  addRootFolderTreeInAllSearchAtom,
  setFolderInAllSearchAtom,
  removeFolderInAllSearchAtom,
  setViewModelInAllSearchAtom,
  removeViewModelInAllSearchAtom,
  removeBulkInAllSearchAtom,
  allSearchFolderTreesAtom,
} from './searchResultState';

export const useSidebarSearchAllRecords = (
  groupId: string,
  isSearchPanelVisible: boolean
): { firstLoaded: boolean } => {
  const currentUserId = useCurrentUserId();
  const currentSessionId = useCurrentSessionId();

  const [firstLoaded, setFirstLoaded] = useState(false);

  const setInitialAllSearchRecords = useSetAtom(setInitialAllSearchRecordsAtom);
  const addWorkspaceInAllSearch = useSetAtom(addWorkspaceInAllSearchAtom);
  const addRootFolderTreeInAllSearch = useSetAtom(addRootFolderTreeInAllSearchAtom);
  const setFolderInAllSearch = useSetAtom(setFolderInAllSearchAtom);
  const removeFolderInAllSearch = useSetAtom(removeFolderInAllSearchAtom);
  const setViewModelInAllSearch = useSetAtom(setViewModelInAllSearchAtom);
  const removeViewModelInAllSearch = useSetAtom(removeViewModelInAllSearchAtom);
  const removeBulkInAllSearch = useSetAtom(removeBulkInAllSearchAtom);
  const folderTrees = useAtomValue(allSearchFolderTreesAtom);

  const workspaceIdsRepoRef = useRef<KeysRepository<WorkspaceId> | null>(null);
  const workspaceEntityReposRef = useRef<Record<WorkspaceId, WorkspaceEntityRepository>>({});
  const folderTreeReposRef = useRef<Record<WorkspaceId, RootFolderTreeRepository>>({});
  const folderReposRef = useRef<Record<WorkspaceId, ObjectRepository<FolderJSON, Folder>>>({});
  const viewModelReposRef = useRef<Record<WorkspaceId, ObjectRepository<ViewModelJSON, ViewModelEntity>>>({});

  useEffect(() => {
    if (!currentUserId) return;
    if (!groupId) return;
    if (!isSearchPanelVisible) return;
    const workspaceEntityRepos = workspaceEntityReposRef.current;
    const folderTreeRepos = folderTreeReposRef.current;
    const folderRepos = folderReposRef.current;
    const viewModelRepos = viewModelReposRef.current;

    if (!workspaceIdsRepoRef.current) {
      workspaceIdsRepoRef.current = new KeysRepository<WorkspaceId>(
        RTDBPath.Workspace.assignedGroupWorkspaceIndexPath(currentUserId, groupId)
      );
    }
    const workspaceIdsRepo = workspaceIdsRepoRef.current;

    // 初回のみワークスペースをまとめて取得する
    if (!firstLoaded) {
      // 初回読込時のみワークスペースをまとめて取得するための関数
      const fetchFirstLoad = async (): Promise<{
        workspaces: Record<WorkspaceId, Workspace>;
        folderTrees: Record<WorkspaceId, RootFolderTree>;
        folders: Record<FolderId, Folder>;
        viewModels: Record<ViewModelId, ViewModelEntity>;
      }> => {
        await GroupIpRestrictionCheckRequest.waitAllow(groupId, currentSessionId);

        const workspaceIds = await workspaceIdsRepo.get();

        const workspaces: Workspace[] = (
          await Promise.all(
            workspaceIds.map(async (id) => {
              const repo = createWorkspaceEntityRepository(id);
              return repo.get();
            })
          )
        ).filter((w): w is Workspace => !!w);

        const folderTrees: Record<WorkspaceId, RootFolderTree> = Object.fromEntries(
          await Promise.all(
            workspaces.map(async (w) => {
              const repo = new RootFolderTreeRepository(w.id);
              folderTreeRepos[w.id] = repo;
              const rootFolderTree = await repo.load();
              return [w.id, rootFolderTree];
            })
          )
        );

        const folderIds: FolderId[] = [];
        Object.values(folderTrees).forEach((folderTree) => {
          folderIds.push(...folderTree.getAllFolderIds());
        });

        const folders: Record<FolderId, Folder> = Object.fromEntries(
          await Promise.all(
            folderIds.map(async (folderId) => {
              const repo = new ObjectRepository(Folder, RTDBPath.Workspace.folderPath(folderId));
              folderRepos[folderId] = repo;
              return [folderId, await repo.get()];
            })
          )
        );

        const viewModelIds: ViewModelId[] = [];
        Object.values(folderTrees).forEach((folderTree) => {
          viewModelIds.push(...folderTree.getAllViewModelIds());
        });

        const viewModels: Record<ViewModelId, ViewModelEntity> = Object.fromEntries(
          await Promise.all(
            viewModelIds.map(async (viewModelId) => {
              const repo = new ObjectRepository(ViewModelEntity, RTDBPath.ViewModel.viewModelPath(viewModelId));
              viewModelRepos[viewModelId] = repo;
              return [viewModelId, await repo.get()];
            })
          )
        );

        return {
          workspaces: Object.fromEntries(workspaces.map((ws) => [ws.id, ws])),
          folderTrees,
          folders,
          viewModels,
        };
      };

      fetchFirstLoad()
        .then((values) => {
          setInitialAllSearchRecords(values);
        })
        .finally(() => {
          setFirstLoaded(true);
        });
      return;
    } else {
      workspaceIdsRepo.addListener(
        // 所属ワークスペースが増えた時
        (workspaceId) => {
          // ワークスペースの監視
          if (!workspaceEntityRepos[workspaceId]) {
            workspaceEntityRepos[workspaceId] = createWorkspaceEntityRepository(workspaceId);
          }
          workspaceEntityRepos[workspaceId].addListener((workspace) => {
            if (!workspace) return;
            addWorkspaceInAllSearch({ workspace });
          });

          // フォルダツリーの監視
          if (!folderTreeRepos[workspaceId]) {
            folderTreeRepos[workspaceId] = new RootFolderTreeRepository(workspaceId);
          }
          folderTreeRepos[workspaceId].on((rootFolderTree) => {
            addRootFolderTreeInAllSearch({ workspaceId, rootFolderTree });
          });
        },
        // 所属ワークスペースが減った時
        (workspaceId) => {
          // ワークスペースの監視解除
          workspaceEntityRepos[workspaceId]?.removeListener();
          delete workspaceEntityRepos[workspaceId];

          // フォルダツリーの監視解除
          folderTreeRepos[workspaceId]?.off();
          delete folderTreeRepos[workspaceId];

          // 削除されたワークスペースに紐づく各種データを削除
          removeBulkInAllSearch({ workspaceId });
        }
      );

      return () => {
        Object.values(workspaceEntityRepos).forEach((repo) => repo.removeListener());
        Object.values(folderTreeRepos).forEach((repo) => repo.off());
        Object.values(folderRepos).forEach((repo) => repo.removeListener());
        Object.values(viewModelRepos).forEach((repo) => repo.removeListener());
      };
    }
  }, [
    firstLoaded,
    groupId,
    currentUserId,
    currentSessionId,
    isSearchPanelVisible,
    setInitialAllSearchRecords,
    addWorkspaceInAllSearch,
    addRootFolderTreeInAllSearch,
    removeBulkInAllSearch,
  ]);

  const previousFolderTrees = usePrevious(folderTrees);

  useEffect(() => {
    if (!isSearchPanelVisible) return;
    // RootFolderTreeのレコードから、全てのフォルダーIDの配列を生成
    const treeToIds = (folderTrees: Record<WorkspaceId, RootFolderTree>): FolderId[] => {
      const ids: FolderId[] = [];
      Object.values(folderTrees).forEach((folderTree) => {
        ids.push(...folderTree.getAllFolderIds());
      });
      return ids;
    };

    const previousIds = treeToIds(previousFolderTrees || {});
    const currentIds = treeToIds(folderTrees);

    const addedIds = currentIds.filter((id) => !previousIds.includes(id));
    const removedIds = previousIds.filter((id) => !currentIds.includes(id));

    addedIds.forEach((id) => {
      const repo = new ObjectRepository(Folder, RTDBPath.Workspace.folderPath(id));
      folderReposRef.current[id] = repo;
      repo.addListener((folder) => {
        if (folder) {
          setFolderInAllSearch({ folder });
        } else {
          removeFolderInAllSearch({ folderId: id });
        }
      });
    });

    removedIds.forEach((id) => {
      folderReposRef.current[id]?.removeListener();
      delete folderReposRef.current[id];
      removeFolderInAllSearch({ folderId: id });
    });
  }, [folderTrees, previousFolderTrees, isSearchPanelVisible, setFolderInAllSearch, removeFolderInAllSearch]);

  useEffect(() => {
    if (!isSearchPanelVisible) return;
    // RootFolderTreeのレコードから、全てのビューモデルIDの配列を生成
    const treeToIds = (folderTrees: Record<WorkspaceId, RootFolderTree>): ViewModelId[] => {
      const ids: ViewModelId[] = [];
      Object.values(folderTrees).forEach((folderTree) => {
        ids.push(...folderTree.getAllViewModelIds());
      });
      return ids;
    };

    const previousIds = treeToIds(previousFolderTrees || {});
    const currentIds = treeToIds(folderTrees);

    const addedIds = currentIds.filter((id) => !previousIds.includes(id));
    const removedIds = previousIds.filter((id) => !currentIds.includes(id));

    addedIds.forEach((id) => {
      const repo = new ObjectRepository(ViewModelEntity, RTDBPath.ViewModel.viewModelPath(id));
      viewModelReposRef.current[id] = repo;
      repo.addListener((viewModel) => {
        if (viewModel) {
          setViewModelInAllSearch({ viewModel });
        } else {
          removeViewModelInAllSearch({ viewModelId: id });
        }
      });
    });

    removedIds.forEach((id) => {
      viewModelReposRef.current[id]?.removeListener();
      delete viewModelReposRef.current[id];
      removeViewModelInAllSearch({ viewModelId: id });
    });
  }, [folderTrees, previousFolderTrees, isSearchPanelVisible, setViewModelInAllSearch, removeViewModelInAllSearch]);

  return { firstLoaded };
};
